import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl, formatApiError } from "@/lib/api";
import { uploadToDraft } from "@/lib/imports";
import { useDraft } from "@/components/import/DraftContext";
import { ImageDropzone } from "@/components/common/ImageDropzone";

function UnassignedImages() {
  const { id } = useDraft();
  const { data } = useQuery({ queryKey: ["imports", id, "images", "tray"],
    queryFn: () => api.get(`/imports/${id}/images`, { params: { status: "unassigned", page_size: 200 } }).then((r) => r.data) });
  const list = (data?.items || []).filter((i) => i.role === "card");
  if (!list.length) return <p className="text-xs text-slate-500" data-testid="import-tray-empty">Every uploaded image is assigned.</p>;
  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-3" data-testid="import-tray-images">
      {list.map((img) => (
        <div key={img.file_id} draggable onDragStart={(e) => e.dataTransfer.setData("text/pf-file", img.file_id)} className="cursor-grab" data-testid={`import-tray-${img.file_id}`}>
          <img src={assetUrl(img.url)} alt={img.filename} className="aspect-[63/88] w-full rounded border border-[#2A3447] bg-black/40 object-contain" />
          <p className="truncate font-mono text-[10px] text-slate-400" title={img.filename}>{img.filename}</p>
          {img.match?.reason && <p className="line-clamp-2 text-[10px] text-amber-300/80">{img.match.reason}</p>}
        </div>
      ))}
    </div>
  );
}

export function ImageTray() {
  const { id, refresh, open, draft } = useDraft();
  const [progress, setProgress] = useState(null);
  const upload = async (files) => {
    setProgress({ done: 0, total: files.length });
    try {
      const results = await uploadToDraft(id, files, (done, total) => setProgress({ done, total }));
      results.filter((r) => !r.ok).slice(0, 5).forEach((r) => toast.error(`${r.filename}: ${r.error}`));
      toast.success(`${results.filter((r) => r.ok).length} image(s) added`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setProgress(null);
      refresh();
    }
  };
  const rematch = async () => {
    try {
      const { data } = await api.post(`/imports/${id}/auto-match`);
      toast.success(`${data.matched} matched · ${data.ambiguous} need you · ${data.unmatched} unmatched`);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <aside className="panel space-y-4 p-5" data-testid="import-image-tray">
      <p className="eyebrow">Card images</p>
      {open && <ImageDropzone multiple compact onFiles={upload} testid="import-images-dropzone" label="Drop finished card images" hint="PNG, JPG or WebP · up to 15 MB each" />}
      {progress && <p className="font-mono text-xs text-slate-400" data-testid="import-images-progress">Uploading {progress.done}/{progress.total}…</p>}
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-slate-400">{draft.summary.unassigned_images} unassigned · drag one onto a card face</p>
        {open && <button className="btn-ghost px-2 py-1 text-xs" onClick={rematch} data-testid="import-rematch"><RefreshCw className="h-3.5 w-3.5" />Match again</button>}
      </div>
      <UnassignedImages />
    </aside>
  );
}
